import {Application, ApplicationStatus} from '../types';

// FNP-998: can this application be decommissioned, and if not, why?
//
// Extracted from DecommissionButton so the decision is testable without a DOM.
// The console's test harness does not mount Mantine's ModalsProvider (see
// accounts/tests/buttons/RemoveRecordButton.test.tsx, which documents the same
// limitation), so modal CONTENT cannot be asserted — but this is the part that
// actually matters, and it is pure.
//
// Mirrors the server-side gate (console-api's validate-deprovisionable.ts).
// Duplicated deliberately: the server is authoritative and refuses regardless,
// but a UI that starts a run doomed to fail at its first step is worse than one
// that explains the problem next to the tab that fixes it.

export type DecommissionBlocker =
    | 'NONE'
    | 'NO_STACK_NAME'
    | 'ALREADY_DELETING'
    | 'ALREADY_DECOMMISSIONED';

export interface DecommissionEligibility {
    eligible: boolean;
    blocker: DecommissionBlocker;
    // The stack that would be deleted. Only set when eligible — a caller must
    // never fall back to guessing one.
    stackName?: string;
    reason?: string;
    // What the admin should do about it, when there is something they can do.
    remedy?: string;
}

export function getDecommissionEligibility(
    record: Application,
    stage: 'dev' | 'test' | 'prod',
): DecommissionEligibility {
    if (record.applicationStatus === ApplicationStatus.DELETING) {
        return {
            eligible: false,
            blocker: 'ALREADY_DELETING',
            reason: 'A teardown is already running for this application.',
        };
    }

    if (record.applicationStatus === ApplicationStatus.DECOMMISSIONED) {
        return {
            eligible: false,
            blocker: 'ALREADY_DECOMMISSIONED',
            reason: 'This application has already been decommissioned.',
        };
    }

    const stackName = record.infraStackNames?.[stage] ?? '';
    if (!stackName) {
        return {
            eligible: false,
            blocker: 'NO_STACK_NAME',
            reason: `No infrastructure stack name is recorded for the ${stage} environment, `
                + 'so the platform does not know which stack to delete. It will not guess: '
                + 'a stack it cannot find looks exactly like one that has already been '
                + 'deleted, and acting on that would report success while the application '
                + 'kept running.',
            remedy: 'Add the stack name on the Provisioning tab, then try again.',
        };
    }

    return {eligible: true, blocker: 'NONE', stackName};
}
